import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import useCreateEmployee from 'app/hooks/useCreateEmployee';

import { addPersonFormSchema } from 'app/utils/schemas/addPerson';

import { CrossOutline, DatePickerInput } from '@aslanpay/portal-common-lib';

import { AddPersonFormFields, AddPersonFormProps } from './types';

function AddPersonForm({ companyId, onSuccess, onCancel }: AddPersonFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    resetField,
    setFocus,
    formState: { errors, isValid, isSubmitting },
  } = useForm<AddPersonFormFields>({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      startDate: undefined,
      leaveDate: undefined,
    },
    resolver: yupResolver(addPersonFormSchema),
  });

  const watchFirstName = watch('firstName');
  const watchLastName = watch('lastName');
  const watchEmail = watch('email');

  const { mutate: createEmployee, isLoading } = useCreateEmployee({
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleFormSubmit = (data: AddPersonFormFields) => {
    createEmployee({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      startDate: data.startDate || undefined,
      companyId,
      payCalendarId: undefined,
      externalPayrollId: undefined,
      salaried: false,
      role: 'EMPLOYEE',
      invite: false,
    });
  };

  const resetAndFocusField = (field: keyof AddPersonFormFields) => {
    resetField(field, { defaultValue: '' });
    setFocus(field);
  };

  return (
    <form id="add-person-form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack spacing="20px">
        <HStack spacing="20px" alignItems="flex-start">
          <FormControl isInvalid={!!errors?.firstName?.message} isRequired>
            <FormLabel>First name</FormLabel>
            <InputGroup>
              <Input type="text" placeholder="Enter their first name" {...register('firstName')} />
              {watchFirstName && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear first name"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('firstName')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.lastName?.message} isRequired>
            <FormLabel>Last name</FormLabel>
            <InputGroup>
              <Input type="text" placeholder="Enter their last name" {...register('lastName')} />
              {watchLastName && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear last name"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('lastName')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
          </FormControl>
        </HStack>

        <FormControl isInvalid={!!errors?.email?.message} isRequired>
          <FormLabel>Company email</FormLabel>
          <InputGroup>
            <Input type="text" placeholder="Enter their email" {...register('email')} />
            {watchEmail && (
              <InputRightElement>
                <IconButton
                  aria-label="Clear email"
                  icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                  variant="link"
                  onClick={() => resetAndFocusField('email')}
                />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.startDate?.message}>
          <FormLabel>Start date</FormLabel>
          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange, onBlur, value } }) => (
              <DatePickerInput
                placeholder="Enter their start date"
                defaultValue={value}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <FormErrorMessage>{errors?.startDate?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.leaveDate?.message}>
          <FormLabel>Leave date (optional)</FormLabel>
          <Controller
            control={control}
            name="leaveDate"
            render={({ field: { onChange, onBlur, value } }) => (
              <DatePickerInput
                placeholder="Enter their leave date (optional)"
                defaultValue={value}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <FormErrorMessage>{errors?.leaveDate?.message}</FormErrorMessage>
        </FormControl>

        <HStack spacing="20px" pt="20px">
          <Text color="system.error" textStyle="extraSmallMedium">
            * mandatory
          </Text>
          <Spacer />
          <Button
            variant="link"
            size="sm"
            onClick={onCancel}
            isDisabled={isSubmitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            form="add-person-form"
            type="submit"
            size="sm"
            isDisabled={!isValid}
            isLoading={isSubmitting || isLoading}
          >
            Add person
          </Button>
        </HStack>
      </Stack>
    </form>
  );
}

export default AddPersonForm;
